import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Input, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import TranslateText from "../../../resuableComponents/TranslateText";
import {
  formatExpiry,
  isValidCardNumber,
  isValidExpiryDate,
  formatCardNumber,
  isEmpty,
  debounce
} from "../../../../Utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { getCardBinData, resetCardBinData } from "../../../../slices/BinSlice";
import { CreditCardFilled } from "@ant-design/icons";
import { getIcon } from "./getCardLogos";
import { selectIsMobileLayout } from "../../../../slices/MobileLayoutSlice";
import PyamentButton from "../../paymentButton/PaymentButton";
import {
  changeCardNumber,
  changeCardHolderName,
  changeExpiryDate,
  changeCardCvc,
  changeBrandName,
  cardPayButtonState,
  getDccQuotations,
  resetDccQuotation,
  setIsSaveCardAsPerRbi,
  selectIsSaveCardAsPerRbi,
  setCardNickName,
  selectCardNickName,
  selectCardFormResetNonce,
} from "../../../../slices/CardSlice";
import { selectSelectedIntrument } from "../../../../slices/SavedInstrumentSlice";
import { creditBankSelected, debitBankSelected, handleEmiPayButton } from "../../../../slices/EmiSlice";
import {
  cardCvvEnteredEvent,
  cardCvvSelectedEvent,
  cardExpiryDateEnteredEvent,
  cardExpiryDateSelectedEvent,
  cardHolderNameEnteredEvent,
  cardHolderNameSelectedEvent,
  cardNumberEnteredEvent,
  cardNumberSelectedEvent,
  saveCardOptionSelectedEvent,
  saveCardOptionRemovedEvent,
  knowMoreButtonClickedEvent,
  knowMoreModalClosedEvent,
} from "../../../../Utils/uiAnalytics";
import { isAutofillInput, useFieldAnalytics } from "../../../../Utils/useFieldAnalytics";
import { selectActivePaymentMethod } from "../../../../slices/PaymentButtonSlice";
import { buildDccRequest } from "../../../../Utils/paymentUtils";
import DccQuotation from "../../dccQuotation/DccQuotation";
import { Checkbox } from 'antd';
import { selectBaseColor } from "../../../../slices/PaymentSessionDetailsSlice";
import RbiGuideLineModal from "../../modals/RbiGuideLineModal";

const { Text } = Typography;
const CARD_CHECK_LENGTH = 9;
const CARD_MAX_LENGTH = 16;
const CARD_MAX_LENGTH_AMEX = 15;
const CARD_MAX_LENGTH_UNIONPAY = 19;

export default function CardPaymentForm(props) {
  const { t, i18n } = useTranslation();
  const { paymentType, cardType, selectedEmiBankTitle } = props;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const isMobileLayout = useSelector((state) => selectIsMobileLayout(state));
  const cardBinData = useSelector((state) => state.binDetails.cardBinData);
  const [cardNumber, setCardNumber] = useState("");
  const [nameOnCard, setNameOnCard] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvcOnCard, setCvcOnCard] = useState("");
  const [brandName, setBrandName] = useState("");
  const [methodEnabled, setMethodEnabled] = useState("");
  const [cardNumberError, setCardNumberError] = useState("");
  const [expiryDateError, setExpiryDateError] = useState("");
  const [nameError, setNameError] = useState("");
  const [cvcError, setCvcError] = useState("");
  const [showRbiGuideLineModal, setShowRbiGuideLineModal] = useState(false);
  const activePaymentMethod = useSelector((state) => selectActivePaymentMethod(state));
  const emiCreditBankSelected = useSelector((state) => creditBankSelected(state));
  const emiDebitBankSelected = useSelector((state) => debitBankSelected(state));
  const selectedInstrument = useSelector((state) => selectSelectedIntrument(state));
  const updatedShopperDetails = useSelector((state) => state.paymentSessionDetails.updatedShopperDetails);
  const dccQuotation = useSelector(state => state.cardDetails.dccQuotation);
  const DCC_CARD_MAX_LENGTH = useSelector(state => state.cardDetails.DCC_CARD_MAX_LENGTH);
  const token = sessionDetails?.token;
  const icons = getIcon(brandName);
  const isPasted = useRef(false);
  const hasEditedNameOnCard = useRef(false);
  const cardNumberInputRef = useRef(null);
  const expiryInputRef = useRef(null);
  const cvcInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const hasAutoFocusedExpiry = useRef(false);
  const baseColor = useSelector((state) => selectBaseColor(state));
  const isSaveCardAsPerRbi = useSelector((state) => selectIsSaveCardAsPerRbi(state));
  const cardNickName = useSelector((state) => selectCardNickName(state));
  const isLoggedIn = useSelector((state) => state.shopperInfo.isLoggedIn);
  const formattedFieldErrorItems = useSelector(state => state.paymentInitiation.formattedFieldErrorItems)
  const cardFormResetNonce = useSelector(selectCardFormResetNonce);
  const lastResetNonceRef = useRef(cardFormResetNonce);
  const { trackFieldSelected, trackFieldEntered, trackAutofillEntered } = useFieldAnalytics();

  const getCVVlength = () => {
    return brandName === "AmericanExpress" ? 4 : 3
  }
  const getCardMaxLength = () => {
    /* brand is unknown until the BIN lookup returns (e.g. right after a paste), so don't clip digits yet */
    if (!brandName) return CARD_MAX_LENGTH_UNIONPAY;
    return brandName.toLocaleLowerCase().includes("unionpay") ? CARD_MAX_LENGTH_UNIONPAY : CARD_MAX_LENGTH
  }
  useEffect(() => {
    /* focus card number as soon as the card section is opened */
    cardNumberInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (cardFormResetNonce === lastResetNonceRef.current) return;
    lastResetNonceRef.current = cardFormResetNonce;
    setCardNumber("");
    setNameOnCard("");
    setExpiryDate("");
    setCvcOnCard("");
    setBrandName("");
    setCardNumberError("");
    setExpiryDateError("");
    setNameError("");
    setCvcError("");
    hasEditedNameOnCard.current = false;
    hasAutoFocusedExpiry.current = false;
    form.resetFields(["cardNumber", "expiryDate", "cvcOnCard", "nameOnCard"]);
    dispatch(resetCardBinData());
    dispatch(resetDccQuotation());
  }, [cardFormResetNonce]);

  useEffect(() => {
    if (hasEditedNameOnCard.current) return;
    const addressShopper = updatedShopperDetails;
    const sessionShopper = sessionDetails?.paymentDetails?.shopper;
    const { firstName, lastName } = !isEmpty(addressShopper) ? addressShopper : (sessionShopper || {});
    const fullName = [firstName, lastName].filter(Boolean).join(" ");
    if (fullName) {
      setNameOnCard(fullName);
      dispatch(changeCardHolderName(fullName));
      form.setFieldsValue({ nameOnCard: fullName });
    }
  }, [updatedShopperDetails, sessionDetails]);
  const handleCardBinDataCall = useCallback(({ token, inputData: value }) => {
    dispatch(getCardBinData({ token, inputData: value }));
  }, []);
  const debouncedCardBinData = useCallback(debounce(handleCardBinDataCall, 500), []);
  const handleCardNumber = (e) => {
    e.target.value = e.target.value.replace(/\D/g, "");
    e.target.value = e.target.value.slice(0, getCardMaxLength());
    const { value } = e.target;

    /* adds space every 4 digits 22223333 -> 2222 3333 */
    const formattedValue = formatCardNumber(value, brandName);
    setCardNumber(formattedValue);
    dispatch(changeCardNumber(value));

    /*** when card length greater than CARD_CHECK_LENGTH then check card scheme and also if the payment is allowed for this card. ***/
    /* card numbers starting with 0 are invalid, so never hit the BIN API for them */
    if (value.startsWith("0")) {
      dispatch(resetCardBinData());
    } else if ((value.length >= CARD_CHECK_LENGTH && isEmpty(cardBinData)) || (isPasted.current && value.length >= CARD_CHECK_LENGTH)) {
      debouncedCardBinData({ token, inputData: value })
    } else if (value.length < CARD_CHECK_LENGTH) {
      dispatch(resetCardBinData());
    }

    /* set form data */
    form.setFieldsValue({ cardNumber: formattedValue });
    if (formattedValue?.length < CARD_CHECK_LENGTH) setBrandName("");

    /* card numbers starting with 0 are invalid on any real network, flag right away instead of waiting for full length */
    if (value.startsWith("0")) {
      setCardNumberError(t("INVALID_CARD_NUMBER"));
    }
  };


  useEffect(() => {
    if (isEmpty(cardNumber)) {
      dispatch(resetCardBinData());
      hasAutoFocusedExpiry.current = false;
    } else {
      const formattedCardNumber = cardNumber.replaceAll(" ", "");
      const isFullLength = formattedCardNumber.length === getCardMaxLength() || (brandName === "AmericanExpress" && formattedCardNumber.length === CARD_MAX_LENGTH_AMEX);
      if (isFullLength) {
        if (!formattedCardNumber.startsWith("0")) debouncedCardBinData({ token, inputData: formattedCardNumber })
        if (!isValidCardNumber(formattedCardNumber) || isEmpty(formattedCardNumber)) {
          setCardNumberError(isEmpty(formattedCardNumber) ? t("REQUIRED") : t("INVALID_CARD_NUMBER"));
          hasAutoFocusedExpiry.current = false;
        } else {
          setCardNumberError('');
          /* auto-advance to expiry once the card number is the full, valid length for the detected brand (15 for Amex, 16 otherwise) */
          if (!hasAutoFocusedExpiry.current) {
            hasAutoFocusedExpiry.current = true;
            expiryInputRef.current?.focus();
          }
        }
      } else {
        hasAutoFocusedExpiry.current = false;
      }
    }
  }, [cardNumber, brandName])

  useEffect(() => {
    if (!isEmpty(formattedFieldErrorItems) && formattedFieldErrorItems.cardNumber) {
      setCardNumberError(formattedFieldErrorItems.cardNumber)
    }
  }, [formattedFieldErrorItems])

  // DCC code starts
  useEffect(() => {
    if (isEmpty(dccQuotation)) return;
    // if Issuer and merchant both have same currency then hide dcc auotation
    if (dccQuotation?.baseMoney?.currencyCode === dccQuotation?.dccQuotationDetails?.dccMoney?.currencyCode) {
      dispatch(resetDccQuotation());
    }
  }, [dccQuotation])
  const handleDccQuotationCall = useCallback(({ token, req }) => {
    dispatch(getDccQuotations({ token, req }));
  }, []);
  const debouncedDccQuotation = useCallback(debounce(handleDccQuotationCall, 500), []);
  useEffect(() => {
    const sanitizedCard = cardNumber.replaceAll(" ", "");
    if (sanitizedCard.length >= DCC_CARD_MAX_LENGTH && !isEmpty(cardBinData) && cardBinData.paymentMethod) {
      let { brand } = cardBinData.paymentMethod;
      let instrument = { brand, accountNumber: sanitizedCard }
      let req = buildDccRequest({ instrument, updatedShopperDetails, sessionDetails });
      debouncedDccQuotation({ token, req })
    } else {
      dispatch(resetDccQuotation());
    }
  }, [cardNumber, cardBinData])
  //DCC code ends

  useEffect(() => {
    /* handle EMI errors when issuer mismatched or not found */
    const { issuerName, issuerTitle } = cardBinData || {};
    const classification = cardBinData?.paymentMethod?.classification;
    let selectedCardValue = cardType?.toLowerCase() === 'credit' ? emiCreditBankSelected : emiDebitBankSelected;
    if (activePaymentMethod?.toLowerCase() === 'emi' && !isEmpty(cardBinData) && !isEmpty(selectedCardValue)) {
      if (isEmpty(issuerName) || isEmpty(classification)) {
        setCardNumberError(t("NO_EMI_FOUND_FOR_CARD"));
      } else if (issuerTitle && issuerName?.toLowerCase() !== selectedCardValue.toLowerCase()) {
        let msz = `${t("CARD_IS")} ${issuerTitle} ${classification} ${t("CARD")}. ${t("ENTER_CARD_NUMBER")} ${selectedEmiBankTitle} ${cardType}  ${t("CARD")}`
        setCardNumberError(msz);
      } else if (issuerTitle && issuerName?.toLowerCase() === selectedCardValue.toLowerCase()) {
        setCardNumberError('');
      }
    } else {
      // setCardNumberError('');
    }
    /* handle EMI errors when issuer mismatched or not found */
  }, [cardBinData, emiCreditBankSelected, selectedEmiBankTitle, emiDebitBankSelected]);

  useEffect(() => {
    setCardNumberError('')
  }, [activePaymentMethod])

  useEffect(() => {
    const sanitizedCard = cardNumber.replaceAll(" ", "");
    /* sets card scheme icon if the card is supported */
    if (!isEmpty(cardBinData)) {
      const brands = cardBinData?.paymentMethod?.brand;
      const methodEnabled = cardBinData?.methodEnabled;

      setMethodEnabled(methodEnabled);
      setBrandName(brands);

      /* a pasted number may exceed the detected brand's max length, trim the extra digits */
      const maxLength = brands?.toLowerCase().includes("unionpay") ? CARD_MAX_LENGTH_UNIONPAY : CARD_MAX_LENGTH;
      if (sanitizedCard.length > maxLength) {
        const trimmed = sanitizedCard.slice(0, maxLength);
        const formattedTrimmed = formatCardNumber(trimmed, brands);
        setCardNumber(formattedTrimmed);
        dispatch(changeCardNumber(trimmed));
        form.setFieldsValue({ cardNumber: formattedTrimmed });
      }
    } else if (sanitizedCard?.length < CARD_CHECK_LENGTH || sanitizedCard?.length === 1) {
      setBrandName("");
      setMethodEnabled("");
      form.setFieldsValue({ cvcOnCard: "" });
      setCvcOnCard("");
    }
  }, [cardBinData]);
  const handleNameOnCard = (e) => {
    const name = e.target.value;
    hasEditedNameOnCard.current = true;
    if (name) setNameError("");
    setNameOnCard(name);
    dispatch(changeCardHolderName(name));
  };
  const handleExpiryDate = (e) => {
    const { value } = e.target;
    let isValidExpiry = false;
    if (value.length === 5) {
      if ((!isValidExpiryDate(value) || isEmpty(value))) setExpiryDateError(isEmpty(value) ? t("REQUIRED") : t("INVALID_EXPIRY"));
      else {
        setExpiryDateError('');
        isValidExpiry = true;
      }
    }
    const formattedValue = formatExpiry(value);
    setExpiryDate(formattedValue);
    dispatch(changeExpiryDate(formattedValue));
    form.setFieldsValue({ expiryDate: formattedValue });
    /* auto-advance to CVV once a valid MM/YY has been entered */
    if (isValidExpiry) cvcInputRef.current?.focus();
  };
  const handleCvcCard = (e) => {
    let inputValue = e.target.value.replace(/\D/g, "");
    setCvcOnCard(inputValue);
    dispatch(changeCardCvc(inputValue));
    form.setFieldsValue({ cvcOnCard: inputValue });
    /* auto-advance to cardholder name once a full CVV has been entered */
    if (inputValue.length === getCVVlength()) {
      nameInputRef.current?.focus();
    }
  };
  const getPayButtonState = () => {
    if (isValidCardNumber(cardNumber.replaceAll(" ", ""))
      && isValidExpiryDate(expiryDate)
      && !isEmpty(nameOnCard)
      && getCVVlength() === cvcOnCard.length
      && isEmpty(cardNumberError)) {
      dispatch(cardPayButtonState(false));
    } else {
      dispatch(cardPayButtonState(true));
    }
  }
  useEffect(() => {
    if (methodEnabled === false) {
      setCardNumberError(t("CARD_NOT_SUPPORTED"));
      dispatch(cardPayButtonState(true));
    } else {
      if (methodEnabled !== false && cardNumberError === t("CARD_NOT_SUPPORTED")) {
        setCardNumberError("");
        return;
      }
      getPayButtonState();
    }
  }, [
    cardNumber,
    nameOnCard,
    expiryDate,
    cvcOnCard,
    cardNumberError,
    expiryDateError,
    cvcError,
    brandName,
    methodEnabled,
  ]);
  useEffect(() => {
    if (brandName) {
      dispatch(changeBrandName(brandName));
    }
  }, [brandName]);
  useEffect(() => {
    if (paymentType === "Emi" && selectedInstrument?.brand !== "CardlessEmi") {
      if (isValidCardNumber(cardNumber.replaceAll(" ", ""))
        && isValidExpiryDate(expiryDate)
        && !isEmpty(nameOnCard)
        && getCVVlength() === cvcOnCard.length
        && selectedInstrument
        && isEmpty(cardNumberError)) {
        dispatch(handleEmiPayButton(false));
      } else {
        dispatch(handleEmiPayButton(true));
      }

      if (methodEnabled === false) {
        dispatch(handleEmiPayButton(true));
      }
    }
  }, [
    paymentType,
    selectedInstrument,
    cardNumber,
    nameOnCard,
    expiryDate,
    cvcOnCard,
    cardNumberError,
    expiryDateError,
    cvcError,
    brandName,
    methodEnabled,
  ]);
  useEffect(() => {
    return () => {
      dispatch(resetCardBinData());
    }
  }, []);
  useEffect(() => {
    const handleLanguageChange = () => {
      const fieldsWithErrors = ['cardNumber', 'nameOnCard', 'expiryDate', 'cvcOnCard']
        .filter((field) =>
          form.getFieldError(field).length > 0
        );

      if (fieldsWithErrors.length > 0) {
        form.validateFields(fieldsWithErrors);
      }
    };

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, form]);

  useEffect(() => {
    const checkboxElements = document.querySelectorAll('.dynamic-checkbox .ant-checkbox-inner');
    const checkedElements = document.querySelectorAll('.dynamic-checkbox.ant-checkbox-wrapper .ant-checkbox-checked .ant-checkbox-inner');

    // Reset all checkboxes
    checkboxElements.forEach((element) => {
      element.style.borderColor = '';
      element.style.backgroundColor = '';
    });

    // Set border color for all checkboxes
    checkboxElements.forEach((element) => {
      element.style.borderColor = baseColor;
    });

    // Set background and border for checked checkboxes
    checkedElements.forEach((element) => {
      element.style.backgroundColor = baseColor;
      element.style.borderColor = baseColor;
    });
  }, [isSaveCardAsPerRbi]);

  /* the checkbox is uncontrolled and resets when this form unmounts (e.g. switching to UPI/wallet), so reset the stored save flag and nickname with it */
  useEffect(() => {
    return () => {
      dispatch(setIsSaveCardAsPerRbi(false));
    };
  }, []);

  return (
    <>
      <Form
        noValidate
        requiredMark={"optional"}
        layout="vertical"
        name="addresss"
        form={form}
        autoComplete="off"
      >
        <div className="domestic-pi-selection-root cardNumberField">
          <Form.Item
            label={
              <Text className="regular14Heading deliveryTitle dynamic-font-family textWrapSingleLine"
                title={t("CARD_NUMBER")}>
                <TranslateText label={"CARD_NUMBER"}>Card Number</TranslateText>*
              </Text>
            }
            name="cardNumber"
            validateTrigger="onBlur"
            rules={[
              {
                required: true,
                message: t("REQUIRED"),
              },
              {
                validator: (_, value) => {
                  if (!isValidCardNumber(value) || isEmpty(value)) return setCardNumberError(isEmpty(value) ? t("REQUIRED") : t("INVALID_CARD_NUMBER")), Promise.reject();
                  else if (isEmpty(cardNumberError)) {
                    return setCardNumberError(""), Promise.resolve();
                  }
                },
              },
            ]}
            validateStatus={cardNumberError ? "error" : ""}
            help={cardNumberError}
          >
            <>
              <Input
                ref={cardNumberInputRef}
                autoComplete="new-password"
                placeholder={`${t("ENTER_CARD_NUMBER_HERE")}`}
                value={cardNumber}
                id="cardNumberInput"
                onFocus={() => {
                  trackFieldSelected(cardNumberSelectedEvent);
                  return activePaymentMethod?.toLowerCase() !== 'emi' && setCardNumberError("")
                }}
                onBlur={() => trackFieldEntered(cardNumberEnteredEvent, cardNumber)}
                onChange={handleCardNumber}
                onInput={(e) => {
                  if (!isAutofillInput(e)) return;
                  const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, getCardMaxLength());
                  trackFieldEntered(cardNumberEnteredEvent, formatCardNumber(digitsOnly, brandName), "autocomplete");
                }}
                onPaste={() => isPasted.current = true}
                inputMode="numeric"
                pattern="[0-9]*"
                addonAfter={
                  icons && icons?.length > 0 ? (
                    icons[0]
                  ) : (
                    <CreditCardFilled className="defaultCardColor" />
                  )
                }
              />
            </>
          </Form.Item>
          <div className="cvvDiv">
            <Form.Item
              label={
                <Text className="regular14Heading deliveryTitle textWrapSingleLine dynamic-font-family"
                  title={t("EXPIRY_DATE")}>
                  <TranslateText label={"EXPIRY_DATE"}>Expiry Date</TranslateText>*
                </Text>
              }
              name="expiryDate"
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: t("REQUIRED"),
                },
                {
                  validator: (_, value) => {
                    if (!isValidExpiryDate(value) || isEmpty(value)) return setExpiryDateError(isEmpty(value) ? t("REQUIRED") : t("INVALID_EXPIRY")), Promise.reject(t("REQUIRED"));
                    else return setExpiryDateError(""), Promise.resolve();
                  },
                },
              ]}
              validateStatus={expiryDateError ? "error" : ""}
              help={expiryDateError}
            >
              <>
                <Input
                  ref={expiryInputRef}
                  placeholder={`${t("MONTH_YEAR")}`}
                  value={expiryDate}
                  onChange={handleExpiryDate}
                  onFocus={() => {
                    trackFieldSelected(cardExpiryDateSelectedEvent);
                    setExpiryDateError("");
                  }}
                  onBlur={() => trackFieldEntered(cardExpiryDateEnteredEvent, expiryDate)}
                  onInput={(e) => {
                    if (!isAutofillInput(e)) return;
                    trackFieldEntered(cardExpiryDateEnteredEvent, formatExpiry(e.target.value), "autocomplete");
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </>
            </Form.Item>
            <Form.Item
              label={
                <>
                  <Text className="regular14Heading deliveryTitle textWrapSingleLine dynamic-font-family"
                    title={t("CARD_CVV")}>
                    <TranslateText label={"CARD_CVV"}>Card CVV</TranslateText>*
                  </Text>
                  <Tooltip
                    placement="right"
                    title={t("CARD_CVV_DESCRIPTION_INFO")}
                  >
                    <img
                      src="/assets/address/help-circle.svg"
                      alt="help-circle"
                      style={{ marginLeft: "4px", width: "16px" }}
                    />
                  </Tooltip>
                </>
              }
              name="cvcOnCard"
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: t("REQUIRED"),
                },
                {
                  validator: (_, value) => {
                    let cvcLength = getCVVlength();
                    // let cvcLength = brandName === "AmericanExpress" ? 4 : 3;
                    if (value.length !== cvcLength || isEmpty(value))
                      return setCvcError(isEmpty(value) ? t("REQUIRED") : t("INVALID_CVV")), Promise.reject(t("REQUIRED"));

                  },
                },
              ]}
              validateStatus={cvcError ? "error" : ""}
              help={cvcError}
            >
              <>
                <Input
                  ref={cvcInputRef}
                  type="password"
                  placeholder={t("ENTER_CARD_CVV")}
                  value={cvcOnCard}
                  onChange={handleCvcCard}
                  maxLength={getCVVlength()}
                  onFocus={() => {
                    trackFieldSelected(cardCvvSelectedEvent);
                    setCvcError("");
                  }}
                  onBlur={() => trackFieldEntered(cardCvvEnteredEvent, cvcOnCard)}
                  onInput={(e) => {
                    if (!isAutofillInput(e)) return;
                    trackFieldEntered(cardCvvEnteredEvent, e.target.value.replace(/\D/g, ""), "autocomplete");
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </>
            </Form.Item>
          </div>
          <Form.Item
            label={
              <Text className="regular14Heading deliveryTitle textWrapSingleLine dynamic-font-family"
                title={t("NAME_ON_CARD")}>
                <TranslateText label={"NAME_ON_CARD"}>Cardholder Name</TranslateText>*
              </Text>
            }
            name="nameOnCard"
            validateTrigger="onBlur"
            rules={[
              {
                required: true,
                message: t("REQUIRED"),
              },
              {
                validator: (_, value) => {
                  if (!value) return setNameError(t("REQUIRED")), Promise.reject();
                  else return setNameError(""), Promise.resolve();
                },
              },
            ]}
            validateStatus={nameError ? "error" : ""}
            help={nameError}
          >
            <>
              <Input
                ref={nameInputRef}
                placeholder={`${t("ENTER_YOUR_NAME")}`}
                value={nameOnCard}
                onFocus={() => {
                  trackFieldSelected(cardHolderNameSelectedEvent);
                  setNameError("");
                }}
                onBlur={() => trackFieldEntered(cardHolderNameEnteredEvent, nameOnCard)}
                onChange={handleNameOnCard}
                onInput={(e) => trackAutofillEntered(e, cardHolderNameEnteredEvent)}
              />
            </>
          </Form.Item>
          {
            isLoggedIn && (
              <div>
                {isSaveCardAsPerRbi && (
                  <div style={{ marginBottom: 12 }}>
                    <Text className="regular14Heading deliveryTitle textWrapSingleLine dynamic-font-family">
                      <TranslateText label={"CARD_NICK_NAME"}>Card Nick Name</TranslateText>
                    </Text>
                    <Input
                      placeholder={t("ENTER_CARD_NICK_NAME", "Enter card nick name")}
                      value={cardNickName}
                      maxLength={50}
                      onChange={(e) => dispatch(setCardNickName(e.target.value))}
                    />
                  </div>
                )}
                <Checkbox className="dynamic-checkbox"
                  onClick={(e) => {
                    if (e.target.checked) {
                      saveCardOptionSelectedEvent();
                    } else {
                      saveCardOptionRemovedEvent();
                    }
                    dispatch(setIsSaveCardAsPerRbi(e.target.checked))
                  }}
                >
                  <span className="save-card-div">
                    <TranslateText label={"SAVE_CARD_AS_PER_RBI"}>Save this Card as per RBI guidelines</TranslateText>.
                  </span>
                </Checkbox>
                <span className="know-more-text" onClick={() => {
                  knowMoreButtonClickedEvent();
                  setShowRbiGuideLineModal(true);
                }}>
                  <TranslateText label={"KNOW_MORE"}>Know more</TranslateText>
                </span>
              </div>
            )
          }
          {
            !isEmpty(dccQuotation) && (<DccQuotation />)
          }
          <div
            className="buttonDiv"
            style={{ display: isMobileLayout ? "none" : "" }}
          >
            <PyamentButton />
          </div>
        </div>
      </Form>
      <RbiGuideLineModal
        isOpen={showRbiGuideLineModal}
        setIsOpen={(value) => {
          if (!value) knowMoreModalClosedEvent();
          setShowRbiGuideLineModal(value);
        }}
      />
    </>
  );
}
